import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Share2, X, Copy, Download, Check, Sparkles,
  Instagram, Music2, Twitter, Linkedin, Facebook,
} from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";

/**
 * SocialShareDialog — multi-platform post generator for a single gallery
 * photo. Calls /api/admin/social/{media_id}/generate to get all 5 platform
 * crops + ARIA-tuned captions, then renders one tile per platform with:
 *   • Preview thumbnail
 *   • Copy caption button
 *   • Download image button
 *   • Native Web Share API "Share" button (Instagram/TikTok cannot post
 *     from the open web — the download + native share is the standard
 *     workflow on iPhone/Android)
 *
 * The endpoint returns cropped images instantly + fallback stub captions,
 * then ARIA's GPT-5 narrative replaces them via the poll endpoint within
 * 60-90s. The dialog polls every 4s and updates each tile live.
 */
const PLATFORM_ICONS = {
  instagram_square: Instagram,
  instagram_reel: Instagram,
  tiktok: Music2,
  twitter: Twitter,
  linkedin: Linkedin,
  facebook: Facebook,
};

const PLATFORM_COLORS = {
  instagram_square: "from-pink-500 to-purple-500",
  instagram_reel:   "from-pink-500 to-orange-500",
  tiktok:           "from-cyan-300 to-pink-500",
  twitter:          "from-sky-400 to-sky-600",
  linkedin:         "from-blue-600 to-blue-800",
  facebook:         "from-blue-500 to-indigo-600",
};

export default function SocialShareDialog({ mediaId, mediaUrl, onClose }) {
  const [loading, setLoading] = useState(true);
  const [postId, setPostId] = useState(null);
  const [variants, setVariants] = useState([]);
  const [seed, setSeed] = useState("");
  const [showSeedField, setShowSeedField] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const pollTimer = useRef(null);

  // Trigger generation on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.post(`/admin/social/${mediaId}/generate`, {
          // Send no `platforms` array → backend defaults to all 6 platforms.
          custom_caption_seed: seed.trim() || null,
        }, { timeout: 30000 });
        if (!alive) return;
        setPostId(r.data.post_id);
        setVariants(r.data.variants || []);
        setLoading(false);
        // Start polling for the LLM-replaced captions
        startPolling(r.data.post_id);
      } catch (e) {
        if (!alive) return;
        toast.error(e?.response?.data?.detail || "Couldn't generate social pack.");
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  const startPolling = (id) => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    let attempts = 0;
    pollTimer.current = setInterval(async () => {
      attempts += 1;
      if (attempts > 30) { clearInterval(pollTimer.current); return; } // ~2min hard cap
      try {
        const r = await api.get(`/admin/social/post/${id}`, { timeout: 15000 });
        const fresh = r.data;
        setVariants(fresh.variants || []);
        // All captions ready → stop polling.
        if ((fresh.variants || []).every(v => v.status === "ready")) {
          clearInterval(pollTimer.current);
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  const regenerate = async () => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    setLoading(true);
    try {
      const r = await api.post(`/admin/social/${mediaId}/generate`, {
        custom_caption_seed: seed.trim() || null,
      }, { timeout: 30000 });
      setPostId(r.data.post_id);
      setVariants(r.data.variants || []);
      startPolling(r.data.post_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't regenerate.");
    } finally { setLoading(false); }
  };

  const copyCaption = async (variantId, caption) => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopiedId(variantId);
      toast.success("Caption copied.");
      setTimeout(() => setCopiedId(null), 1500);
      trackShare(variantId, "copy_link");
    } catch { toast.error("Couldn't copy."); }
  };

  const downloadVariant = async (platformId) => {
    if (!postId) return;
    const token = localStorage.getItem("auth_token");
    const url = `${API_BASE}/admin/social/post/${postId}/image/${platformId}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `aristocratsja_${platformId}_${(postId || "x").slice(0, 8)}.jpg`;
      a.click();
      URL.revokeObjectURL(objUrl);
      trackShare(platformId, "download");
      toast.success(`${platformId} version downloaded.`);
    } catch (e) {
      toast.error("Download failed.");
    }
  };

  const nativeShare = async (variant) => {
    // Web Share API — works best on mobile. On desktop browsers without
    // navigator.share, we fall back to download.
    if (!navigator.share) {
      downloadVariant(variant.platform_id);
      return;
    }
    try {
      // Convert the b64 image to a File so the share dialog includes
      // the actual image (not just text).
      const b64 = variant.image_b64;
      const byteString = atob(b64);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const file = new File(
        [ab],
        `aristocratsja_${variant.platform_id}.jpg`,
        { type: "image/jpeg" },
      );
      await navigator.share({
        title: "Aristocrats JA Signature Events",
        text: variant.caption || "",
        files: [file],
      });
      trackShare(variant.platform_id, "web_share_api");
    } catch (e) {
      if (e?.name !== "AbortError") {
        // User dismissed = fine. Real error = fall back to download.
        downloadVariant(variant.platform_id);
      }
    }
  };

  const trackShare = async (channel, via) => {
    if (!postId) return;
    try { await api.post(`/admin/social/post/${postId}/track-share`, { channel, via }); }
    catch { /* fire and forget */ }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
        onClick={onClose}
        data-testid="social-share-dialog"
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-5xl max-h-[92vh] overflow-hidden bg-luxury-charcoal border border-gold/30 flex flex-col"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 p-5 border-b border-white/5 shrink-0">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-body uppercase tracking-[0.25em] text-gold mb-1 flex items-center gap-1.5">
                <Sparkles size={11} /> ARIA Social Studio
              </p>
              <h2 className="text-white font-heading text-2xl">Share to Social</h2>
              <p className="text-white/40 text-xs font-body mt-1">
                One photo · 5 platform-ready versions · captions tuned per network.
              </p>
            </div>
            <button onClick={onClose} data-testid="social-dialog-close" className="text-white/40 hover:text-white p-1">
              <X size={18} />
            </button>
          </div>

          {/* Seed input (optional admin override) */}
          <div className="p-4 border-b border-white/5 shrink-0">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-body uppercase tracking-wider text-white/40">Caption hint (optional)</p>
              <button onClick={() => setShowSeedField(!showSeedField)} className="text-[10px] text-gold hover:text-gold/80 font-body uppercase tracking-wider">
                {showSeedField ? "Hide" : "Customise"}
              </button>
            </div>
            {showSeedField && (
              <div className="flex gap-2 mt-2">
                <input
                  data-testid="social-seed-input"
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                  placeholder="e.g. 'yacht reveal at sunset' — ARIA rewrites for each network"
                  maxLength={600}
                  className="flex-1 bg-luxury-black border border-white/10 text-white text-sm px-3 py-2 font-body placeholder:text-white/30 focus:border-gold/50 outline-none"
                />
                <button onClick={regenerate} disabled={loading} data-testid="social-regenerate" className="bg-gold hover:bg-gold/90 text-luxury-black px-4 py-2 text-xs font-body uppercase tracking-wider font-medium disabled:opacity-50">
                  Regenerate
                </button>
              </div>
            )}
          </div>

          {/* Variants grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="text-center py-16">
                <Sparkles size={32} className="text-gold/40 mx-auto mb-3 animate-pulse" />
                <p className="text-white/40 text-sm font-body animate-pulse">ARIA is cropping for every platform...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="social-variants-grid">
                {variants.map((v) => {
                  const Icon = PLATFORM_ICONS[v.platform_id] || Share2;
                  const gradient = PLATFORM_COLORS[v.platform_id] || "from-gold to-gold/70";
                  return (
                    <div key={v.platform_id} className="bg-luxury-black border border-white/5 hover:border-gold/30 transition-colors flex flex-col" data-testid={`social-variant-${v.platform_id}`}>
                      {/* Preview */}
                      <div className={`relative bg-gradient-to-br ${gradient} aspect-square overflow-hidden`}>
                        {v.image_b64 ? (
                          <img src={`data:image/jpeg;base64,${v.image_b64}`} alt={v.platform_label} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Icon size={40} className="text-white/50" /></div>
                        )}
                        <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 backdrop-blur px-2 py-1">
                          <Icon size={12} className="text-white" />
                          <span className="text-white text-[10px] font-body uppercase tracking-wider">{v.platform_label}</span>
                        </div>
                        <div className="absolute top-2 right-2 text-[10px] font-body text-white/80 bg-black/60 px-1.5 py-0.5">{v.aspect}</div>
                      </div>

                      {/* Caption */}
                      <div className="p-3 flex-1 flex flex-col">
                        <div className="flex items-center gap-1.5 mb-2">
                          <span className="text-[10px] font-body uppercase tracking-wider text-white/40">{v.width}×{v.height}</span>
                          {v.status === "pending_caption" ? (
                            <span data-testid={`pending-${v.platform_id}`} className="text-[10px] text-gold animate-pulse">ARIA is writing...</span>
                          ) : v.ai_generated ? (
                            <span className="text-[10px] text-gold flex items-center gap-1"><Sparkles size={9} /> ARIA</span>
                          ) : (
                            <span className="text-[10px] text-white/30">stub</span>
                          )}
                        </div>
                        <p className="text-white/80 text-xs font-body leading-snug whitespace-pre-wrap flex-1 line-clamp-6">
                          {v.caption || "—"}
                        </p>

                        {/* Actions */}
                        <div className="flex gap-1 mt-3 pt-3 border-t border-white/5">
                          <button onClick={() => copyCaption(v.platform_id, v.caption || "")} data-testid={`copy-${v.platform_id}`} className="flex-1 text-[10px] font-body uppercase tracking-wider text-white/60 hover:text-gold border border-white/10 hover:border-gold/40 px-2 py-1.5 transition-colors flex items-center justify-center gap-1.5">
                            {copiedId === v.platform_id ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Caption</>}
                          </button>
                          <button onClick={() => downloadVariant(v.platform_id)} data-testid={`download-${v.platform_id}`} className="flex-1 text-[10px] font-body uppercase tracking-wider text-white/60 hover:text-gold border border-white/10 hover:border-gold/40 px-2 py-1.5 transition-colors flex items-center justify-center gap-1.5">
                            <Download size={11} /> Save
                          </button>
                          <button onClick={() => nativeShare(v)} data-testid={`share-${v.platform_id}`} className="flex-1 text-[10px] font-body uppercase tracking-wider bg-gold/15 text-gold hover:bg-gold/25 border border-gold/30 px-2 py-1.5 transition-colors flex items-center justify-center gap-1.5 font-medium">
                            <Share2 size={11} /> Share
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer tip */}
          <div className="p-3 border-t border-white/5 shrink-0 text-center">
            <p className="text-white/30 text-[10px] font-body">
              Instagram / TikTok require the native app — tap <span className="text-gold">Share</span> on mobile (Web Share API) or
              <span className="text-gold"> Save</span> then upload manually.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
